import React, { useMemo, useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import ProjectCarousel from "../components/ProjectCarousel";
import HeroSection from "../components/HeroSection";
import { get } from "../api/config";

function StatCard({ title, value, footer, color, link }) {
  const colorClasses = useMemo(() => {
    switch (color) {
      case "blue":
        return {
          panel: "bg-white border-blue-500 hover:shadow-lg hover:-translate-y-1",
          title: "text-blue-600",
          value: "text-blue-700",
        };
      case "red":
        return {
          panel: "bg-white border-red-500 hover:shadow-lg hover:-translate-y-1",
          title: "text-red-600",
          value: "text-red-700",
        };
      case "orange":
        return {
          panel: "bg-white border-orange-500 hover:shadow-lg hover:-translate-y-1",
          title: "text-orange-600",
          value: "text-orange-700",
        };
      case "green":
        return {
          panel: "bg-white border-green-500 hover:shadow-lg hover:-translate-y-1",
          title: "text-green-600",
          value: "text-green-700",
        };
      case "purple":
        return {
          panel: "bg-white border-purple-500 hover:shadow-lg hover:-translate-y-1",
          title: "text-purple-600",
          value: "text-purple-700",
        };
      default:
        return {
          panel: "bg-white hover:shadow-lg hover:-translate-y-1",
          title: "text-gray-700",
          value: "text-gray-900",
        };
    }
  }, [color]);

  const cardContent = (
    <div
      className={`border rounded-xl shadow-sm p-6 text-center cursor-pointer transition-all duration-300 ${colorClasses.panel}`}
    >
      <div className={`text-lg font-semibold ${colorClasses.title}`}>
        {title}
      </div>
      <div className={`mt-3 text-2xl font-bold ${colorClasses.value}`}>
        {value}
      </div>
      {footer ? (
        <div className="mt-4 text-xs text-gray-500">{footer}</div>
      ) : null}
    </div>
  );

  if (link) {
    return <Link to={link}>{cardContent}</Link>;
  }

  return cardContent;
}

function ImageStatCard({ image, title, value, footer }) {
  return (
    <div className="relative overflow-hidden rounded-lg">
      <img src={image} alt={title} className="w-full h-auto" />
      <div className="absolute inset-0" />
      <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center text-white">
        <div className="text-3xl font-extrabold leading-tight drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]">
          {title}
        </div>
        <div className="mt-3 text-2xl font-bold drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]">
          {value}
        </div>
        {footer ? (
          <div className="mt-5 text-xs opacity-90 drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function Home() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Sample data - will be replaced with API calls
  const [projectStats, setProjectStats] = useState({
    totalProjects: 25,
    completedProjects: 7,
    ongoingProjects: 10,
    pendingProjects: 8,
    completedValue: 23700000, // 2.37 crores in rupees
    ongoingValue: 57000000, // 5.7 crores in rupees
    pendingValue: 73500000, // 7.35 crores in rupees
  });

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      const response = await get("public/statistics");
      setStats(response.data);
      
      // Note: You'll need to fetch project counts from the projects API
      // For now, we're using the sample data above
    } catch (err) {
      setError("Failed to load statistics");
      console.error("Error fetching statistics:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "₹ 0";
    const numAmount = parseFloat(amount);

    if (numAmount >= 10000000) {
      return `₹ ${(numAmount / 10000000).toFixed(1)} Cr`;
    } else if (numAmount >= 100000) {
      return `₹ ${(numAmount / 100000).toFixed(1)} L`;
    } else if (numAmount >= 1000) {
      return `₹ ${(numAmount / 1000).toFixed(1)} K`;
    }

    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(numAmount);
  };

  const formatNumber = (num) => {
    if (!num) return "0";
    return new Intl.NumberFormat("en-IN").format(parseFloat(num));
  };

  const getCurrentDateFooter = () => {
    const date = new Date();
    return `as of ${date.getDate()} • ${date.toLocaleDateString("en-IN", {
      month: "long",
      year: "numeric",
    })}`;
  };

  const publicImage = (name) => `${process.env.PUBLIC_URL}/images/${name}`;

  if (loading) {
    return (
      <div className="">
        <HeroSection />
        <div className="mx-auto max-w-6xl px-4">
          <div className="text-center py-8">
            <div className="text-gray-600">Loading statistics...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="">
        <HeroSection />
        <div className="mx-auto max-w-6xl px-4">
          <div className="text-center py-8">
            <div className="text-red-600 mb-4">{error}</div>
            <button
              onClick={fetchStatistics}
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Calculate percentage for utilization
  const utilizationPercentage = stats?.total_funds ? 
    ((parseFloat(stats.total_utilized) / parseFloat(stats.total_funds)) * 100).toFixed(1) : 0;
  
  const pendingPercentage = stats?.total_funds ? 
    ((parseFloat(stats.total_remaining) / parseFloat(stats.total_funds)) * 100).toFixed(1) : 0;

  return (
    <div className="">
      {/* Hero banner */}
      <HeroSection />

      <div className="mx-auto max-w-6xl px-4">
        {/* Top meta row */}
        <div className="flex items-center justify-between text-sm text-gray-600 py-3">
          <div className="flex items-center gap-2">
            {/* Add any meta information here */}
          </div>
        </div>

        {/* Title */}
        <h1 className="text-xl font-semibold text-center mb-6 text-green-600">
          MP Funds Dashboard
        </h1>

        {/* Stats grid (2 cards per row) using provided images */}
        {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ImageStatCard
            image={publicImage("Group 34.png")}
            title="Total Funds"
            value={formatCurrency(stats?.total_funds || "0")}
            footer={getCurrentDateFooter()}
          />
          <ImageStatCard
            image={publicImage("Group 35.png")}
            title="Allocated Funds"
            value={formatCurrency(stats?.total_allocated || "0")}
            footer={`${stats?.allocation_count || 0} allocations`}
          />
          <ImageStatCard
            image={publicImage("Group 36.png")}
            title="Utilized Funds"
            value={formatCurrency(stats?.total_utilized || "0")}
            footer={`${utilizationPercentage}% utilized`}
          />
          <ImageStatCard
            image={publicImage("Group 37.png")}
            title="Remaining Funds"
            value={formatCurrency(stats?.total_remaining || "0")}
            footer={`${pendingPercentage}% remaining`}
          />
        </div> */}

        {/* Project Statistics Overview */}
        <div className="mt-8 bg-gradient-to-r from-blue-50 to-white rounded-2xl border border-blue-100 p-8 shadow-lg">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            Project Statistics Overview
          </h2>
          
          {/* Main Project Stats Row */}
          <div className="mb-8 bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="flex flex-col md:flex-row md:items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Total Projects Recommended
                </h3>
                <p className="text-gray-600 mt-1">
                  Across all categories and statuses
                </p>
              </div>
              <div className="flex items-center space-x-6 mt-4 md:mt-0">
                <div className="text-center">
                  <div className="text-4xl font-bold text-blue-600">
                    {projectStats.totalProjects}
                  </div>
                  <div className="text-sm text-gray-500">Projects</div>
                </div>
                <div className="h-12 w-px bg-gray-300"></div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600">
                    {formatCurrency(projectStats.totalProjects * 6168000)} {/* Assuming average project cost */}
                  </div>
                  <div className="text-sm text-gray-500">Total Value</div>
                </div>
              </div>
            </div>
          </div>

          {/* Detailed Project Status Breakdown */}
          <div className="space-y-6">
            {/* Completed Projects */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200 p-6 hover:shadow-lg transition-all duration-300">
              <div className="flex flex-col md:flex-row md:items-center justify-between">
                <div className="flex items-start space-x-4">
                  <div className="bg-green-100 p-3 rounded-lg">
                    <span className="text-2xl">✅</span>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">
                      Completed Projects
                    </h4>
                    <p className="text-gray-600 mt-1">
                      Fully implemented and operational
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-8 mt-4 md:mt-0">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-700">
                      {projectStats.completedProjects}
                    </div>
                    <div className="text-sm text-gray-600">Projects</div>
                  </div>
                  <div className="h-12 w-px bg-green-300"></div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-800">
                      {formatCurrency(projectStats.completedValue)}
                    </div>
                    <div className="text-sm text-gray-600">Total Value</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Ongoing Projects */}
            <div className="bg-gradient-to-r from-yellow-50 to-amber-50 rounded-xl border border-yellow-200 p-6 hover:shadow-lg transition-all duration-300">
              <div className="flex flex-col md:flex-row md:items-center justify-between">
                <div className="flex items-start space-x-4">
                  <div className="bg-yellow-100 p-3 rounded-lg">
                    <span className="text-2xl">🚧</span>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">
                      Construction Ongoing
                    </h4>
                    <p className="text-gray-600 mt-1">
                      Currently under construction
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-8 mt-4 md:mt-0">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-yellow-700">
                      {projectStats.ongoingProjects}
                    </div>
                    <div className="text-sm text-gray-600">Projects</div>
                  </div>
                  <div className="h-12 w-px bg-yellow-300"></div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-yellow-800">
                      {formatCurrency(projectStats.ongoingValue)}
                    </div>
                    <div className="text-sm text-gray-600">Total Value</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Pending Projects */}
            <div className="bg-gradient-to-r from-red-50 to-pink-50 rounded-xl border border-red-200 p-6 hover:shadow-lg transition-all duration-300">
              <div className="flex flex-col md:flex-row md:items-center justify-between">
                <div className="flex items-start space-x-4">
                  <div className="bg-red-100 p-3 rounded-lg">
                    <span className="text-2xl">⏳</span>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">
                      Pending for Sanction
                    </h4>
                    <p className="text-gray-600 mt-1">
                      Awaiting district authorities approval
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-8 mt-4 md:mt-0">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-red-700">
                      {projectStats.pendingProjects}
                    </div>
                    <div className="text-sm text-gray-600">Projects</div>
                  </div>
                  <div className="h-12 w-px bg-red-300"></div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-800">
                      {formatCurrency(projectStats.pendingValue)}
                    </div>
                    <div className="text-sm text-gray-600">Total Value</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Fund Allocation Summary */}
        <div className="mt-8 bg-gradient-to-r from-purple-50 to-white rounded-2xl border border-purple-100 p-8 shadow-lg">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            Funds Summary
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-6 text-center border border-blue-200 hover:shadow-lg transition-all duration-300">
              <div className="text-3xl font-bold text-blue-600 mb-2">
                {formatCurrency(stats?.total_funds || "0")}
              </div>
              <div className="text-gray-700 font-medium">Total Funds</div>
              <div className="text-sm text-gray-500 mt-2">
                Available for allocation
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-6 text-center border border-green-200 hover:shadow-lg transition-all duration-300">
              <div className="text-3xl font-bold text-green-600 mb-2">
                {utilizationPercentage}%
              </div>
              <div className="text-gray-700 font-medium">Funds Utilized</div>
              <div className="text-sm text-gray-500 mt-2">
                {formatCurrency(stats?.total_utilized || "0")} of {formatCurrency(stats?.total_allocated || "0")} allocated
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-6 text-center border border-orange-200 hover:shadow-lg transition-all duration-300">
              <div className="text-3xl font-bold text-orange-600 mb-2">
                {pendingPercentage}%
              </div>
              <div className="text-gray-700 font-medium">Funds Available</div>
              <div className="text-sm text-gray-500 mt-2">
                {formatCurrency(stats?.total_remaining || "0")} remaining
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats Cards */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Total Allocations"
            value={stats?.allocation_count || 0}
            color="blue"
            footer={`${stats?.fund_sources?.length || 0} fund sources`}
          />
          <StatCard
            title="Fund Sources"
            value={stats?.fund_sources?.length || 0}
            color="purple"
            footer="Active funding channels"
          />
          <StatCard
            title="Active Projects"
            value={stats?.utilization_by_project?.length || 0}
            color="green"
            footer="With fund allocations"
          />
          <StatCard
            title="Avg Utilization"
            value={stats?.utilization_by_project?.length > 0 ? 
              `${(stats.utilization_by_project.reduce((sum, p) => sum + (p.utilization_percentage || 0), 0) / stats.utilization_by_project.length).toFixed(0)}%` : "0%"
            }
            color="orange"
            footer="Per allocated project"
          />
        </div>

        {/* Project Utilization Details */}
        {stats?.utilization_by_project && stats.utilization_by_project.length > 0 && (
          <div className="mt-8 bg-white rounded-2xl border border-gray-200 p-8 shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              Project Utilization Details
            </h3>
            <div className="space-y-4">
              {stats.utilization_by_project.map((project, index) => (
                <div key={index} className="flex flex-col md:flex-row md:items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-all duration-200">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">{project.project_name}</h4>
                    <div className="flex items-center space-x-4 mt-2">
                      <span className="text-sm text-gray-600">
                        Allocated: {formatCurrency(project.total_allocated)}
                      </span>
                      <span className="text-sm text-gray-600">
                        Utilized: {formatCurrency(project.total_utilized)}
                      </span>
                    </div>
                  </div>
                  <div className="mt-2 md:mt-0">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      project.utilization_percentage === 100 
                        ? "bg-green-100 text-green-800"
                        : project.utilization_percentage > 0
                        ? "bg-blue-100 text-blue-800"
                        : "bg-yellow-100 text-yellow-800"
                    }`}>
                      {project.utilization_percentage || 0}% Utilized
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Promo banner */}
        <div className="mt-8">
          <img
            src={`${process.env.PUBLIC_URL}/images/Rectangle 163.png`}
            alt="Promotional banner"
            className="w-full h-auto rounded-lg"
            loading="lazy"
          />
        </div>

        {/* Projects carousel */}
        <ProjectCarousel />
      </div>
    </div>
  );
}

export default Home;